import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MarkdownViewer } from '@nineeng/form-components';
import request from '@nineeng/request';
import { PERMISSIONS } from '@repo/shared/domain';
import { GroupContact } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getPartnerCompany, getPartnerPortalSettings, getPartnerCompanyTasks } from 'src/api/partner';
import { getVerifyPermission } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { Button } from '@repo/components';
import { FormDialog } from '@repo/components';
import { Alert } from '@repo/components';
import { CopyToClipboardIcon } from '@repo/components';
import { DataGrid, DataGridColumn } from '@repo/components';
import {
	ConfirmDialog,
	Badge,
	Spinner,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@repo/components';
import { AddOwnerForm } from '@repo/forms';
import { PartnerCompanyContactForm } from '@repo/forms';
import { Phone, Mail, Plus, MoreVertical, Trash2, Pencil } from 'lucide-react';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/information')({
	component: RouteComponent,
});

function RouteComponent() {
	const { user: authenticatedUser, loading } = useAuthenticatedUser();
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Information');
	const navigate = useNavigate();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions);
	const queryClient = useQueryClient();
	const [ownerToDelete, setOwnerToDelete] = useState<string | null>(null);
	const [isAddOwnerDialogOpen, setIsAddOwnerDialogOpen] = useState(false);
	const [isPartnerCompanyContactDialogOpen, setIsPartnerCompanyContactDialogOpen] = useState(false);
	const [isDeletePartnerCompanyContactDialogOpen, setIsDeletePartnerCompanyContactDialogOpen] = useState(false);
	const [contactIdToDelete, setContactIdToDelete] = useState<string | null>(null);
	const [partnerCompanyContactData, setPartnerCompanyContactData] = useState<GroupContact | null>(null);

	const {
		data: partnerCompany,
		isLoading: companyLoading,
		error: _getPartnerCompanyError,
	} = useQuery({
		queryKey: ['partner-company', partnerId],
		queryFn: () => getPartnerCompany(partnerId),
	});

	const {
		data: partnerPortalSettings,
		isLoading: partnerPortalSettingsLoading,
		error: _getPartnerPortalSettignsError,
	} = useQuery({
		queryKey: ['partner-portal-settings'],
		queryFn: () => getPartnerPortalSettings(),
	});

	const {
		data: tasksResponse,
		isLoading: tasksLoading,
		error: _getPartnerCompanyTasksError,
	} = useQuery({
		queryKey: ['partner-company-tasks', partnerId],
		queryFn: () => getPartnerCompanyTasks(partnerId),
	});
	const tasks = tasksResponse?.data;
	const tasksTodo = tasks?.filter((t) => t.status === 'todo') || [];

	if (loading || partnerPortalSettingsLoading || companyLoading || tasksLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!partnerPortalSettings) {
		return (
			<div
				className="mt-2 flex flex-col items-center justify-start"
				style={{
					marginTop: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
					flexDirection: 'column',
				}}
			>
				<div className="flex gap-2">
					<div className="min-w-[320px]">
						<p>No settings defined</p>
					</div>
				</div>
			</div>
		);
	}

	if (!partnerCompany) {
		return (
			<div
				className="mt-2 flex flex-col items-center justify-start"
				style={{
					marginTop: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
					flexDirection: 'column',
				}}
			>
				<div className="flex gap-2">
					<div className="min-w-[320px]">
						<p>No Partner company found for this id</p>
					</div>
				</div>
			</div>
		);
	}

	const addOwners = async (formData) => {
		try {
			await request.post(`/api/partner-portal/groups/${partnerCompany.id}/add-owners`, formData);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully added owner(s).',
			});
			queryClient.invalidateQueries({ queryKey: ['partner-company', partnerId] });
			setIsAddOwnerDialogOpen(false);
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to add owner(s).',
			});
		}
	};

	const deleteOwner = async (ownerId: string) => {
		await request.post(`/api/partner-portal/groups/${partnerCompany.id}/delete-owner/${ownerId}`);
		EventEmitter.emit('toast', {
			type: 'success',
			message: 'Successfully removed company admin.',
		});
		queryClient.invalidateQueries({ queryKey: ['partner-company', partnerId] });
		setOwnerToDelete(null);
	};

	const handleOpenPartnerCompanyContactDialog = (groupContact?: GroupContact) => {
		setIsPartnerCompanyContactDialogOpen(true);
		setPartnerCompanyContactData(groupContact || null);
	};

	const handleCloseGroupContactDialog = () => {
		setIsPartnerCompanyContactDialogOpen(false);
		setPartnerCompanyContactData(null);
	};

	const addPartnerCompanyContact = async (formData) => {
		try {
			await request.post(`/api/partner-portal/groups/${partnerCompany.id}/contact`, formData);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully added partner company contact.',
			});
			queryClient.invalidateQueries({ queryKey: ['partner-company', partnerId] });
			setIsPartnerCompanyContactDialogOpen(false);
			setPartnerCompanyContactData(null);
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to add partner company contact.',
			});
		}
	};

	const updatePartnerCompanyContact = async (formData, contactId) => {
		try {
			await request.put(`/api/partner-portal/groups/${partnerCompany.id}/contact/${contactId}`, formData);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully updated partner company contact.',
			});
			queryClient.invalidateQueries({ queryKey: ['partner-company', partnerId] });
			setIsPartnerCompanyContactDialogOpen(false);
			setPartnerCompanyContactData(null);
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to update partner company contact.',
			});
		}
	};

	const handleOpenDeletePartnerCompanyContactDialog = (id: string) => {
		setContactIdToDelete(id);
		console.log('Set Contact to delete', id);
		setIsDeletePartnerCompanyContactDialogOpen(true);
	};

	const handleCloseDeletePartnerCompanyContactDialog = () => {
		setContactIdToDelete(null);
		setIsDeletePartnerCompanyContactDialogOpen(false);
	};

	const deletePartnerCompanyContact = async () => {
		try {
			await request.del(`/api/partner-portal/groups/${partnerCompany.id}/contact/${contactIdToDelete}`);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully deleted partner company contact.',
			});
			queryClient.invalidateQueries({ queryKey: ['partner-company', partnerId] });
			setIsDeletePartnerCompanyContactDialogOpen(false);
			setContactIdToDelete(null);
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to delete partner company contact.',
			});
		}
	};

	const handleViewTask = () => {
		navigate({ to: `/partner/${partnerCompany?.id}/tasks` });
		return null;
	};

	const actionsMenu = (id: string, rowData: GroupContact) => {
		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="sm" className="flex h-6 w-6 p-0 data-[state=open]:bg-muted">
						<MoreVertical className="h-3 w-3" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-[160px]">
					<DropdownMenuItem key={'edit-contact'} onClick={() => handleOpenPartnerCompanyContactDialog(rowData)}>
						<Pencil />
						Edit contact information
					</DropdownMenuItem>
					<DropdownMenuItem key={'delete-contact'} onClick={() => handleOpenDeletePartnerCompanyContactDialog(id)}>
						<Trash2 />
						Delete contact information
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	const columns: DataGridColumn<GroupContact>[] = [
		{
			headerName: 'Type',
			field: 'type',
			flex: 1,
		},
		{
			headerName: 'Name',
			field: 'name',
			width: 120,
		},
		{
			field: 'email',
			headerName: '',
			width: 10,
			renderCell: (c) => c.value && <CopyToClipboardIcon Icon={Mail} value={c.value} side="top" align="center" />,
			sortable: false,
		},
		{
			field: 'phone',
			headerName: '',
			width: 10,
			renderCell: (c) => c.value && <CopyToClipboardIcon Icon={Phone} value={c.value} side="top" align="center" />,
			sortable: false,
		},
		{
			field: 'id',
			headerName: '',
			width: 10,
			renderCell: (c) => actionsMenu(c.value, c.row),
			sortable: false,
		},
	];

	const welcomeMessage =
		partnerPortalSettings.settings?.['welcomeMessage'] ||
		`# Welcome
	This is the default welcome message`;
	const links = partnerPortalSettings.settings?.links || [];
	const helpLinks = partnerPortalSettings.settings?.helpLinks || [];
	const admins = partnerCompany?.admins || [];
	const partnerCompanyData = [
		partnerCompany?.companyData?.addressLine1,
		partnerCompany?.companyData?.addressLine2,
		partnerCompany?.companyData?.postalCode &&
			partnerCompany?.companyData?.place &&
			`${partnerCompany?.companyData?.postalCode} ${partnerCompany?.companyData?.place}`,
		partnerCompany?.companyData?.vatNumber,
	].filter(Boolean);

	return (
		<div className="mt-2 flex flex-col items-center justify-start">
			{tasksTodo.length > 0 && (
				<Alert className="flex flex-row items-center justify-between max-w-[1024px] w-full mt-2 mb-2 text-white bg-[#0288D1]">
					<div>This Partner company has open tasks to complete</div>
					<Button
						size="sm"
						onClick={handleViewTask}
						className="text-sm font-bold bg-[inherit] shadow-none hover:bg-[inherit]"
					>
						View Tasks
					</Button>
				</Alert>
			)}
			<div className="max-w-[1024px] w-full flex gap-2 mt-2">
				{ownerToDelete && (
					<ConfirmDialog
						confirmAction={() => deleteOwner(ownerToDelete)}
						cancelAction={() => setOwnerToDelete(null)}
						isOpen={!!ownerToDelete}
					>
						Are you sure you want to remove this partner company admin?
					</ConfirmDialog>
				)}
				<div className="min-w-[320px] max-w-[480px]">
					<div className="p-4 border border-gray-300 rounded bg-white shadow-sm">
						<p className="font-bold mb-1">Your Partner Company</p>
						<p className="font-bold">{partnerCompany.name}</p>
						{partnerCompanyData.map((groupDataElement) => (
							<p>{groupDataElement}</p>
						))}
					</div>
					{links.length > 0 && (
						<div className="p-4 mt-2 border border-gray-300 rounded bg-white shadow-sm">
							<p className="font-bold mb-1">Links</p>
						</div>
					)}
					{admins.length > 0 && (
						<div className="p-4 mt-2 border border-gray-300 rounded bg-white shadow-sm">
							<p className="font-bold mb-1">Your partner company's admins</p>
							<div className="min-h-[29px] flex flex-wrap gap-0.5">
								{admins.map((admin) => (
									<Badge
										key={admin.id}
										variant="default"
										onDelete={admin.id !== authenticatedUser?.id ? () => setOwnerToDelete(admin.id) : undefined}
									>
										{admin.email}
									</Badge>
								))}
								{verifyPermission([PERMISSIONS.partnerCompanyUsersCreate]) && (
									<Badge onClick={() => setIsAddOwnerDialogOpen(true)}>+</Badge>
								)}
							</div>
						</div>
					)}
					{helpLinks.length > 0 && (
						<div className="p-4 mt-2 border border-gray-300 rounded bg-white shadow-sm">
							<p className="font-bold mb-1">Get Help</p>
							<div className="flex flex-col gap-1">
								{helpLinks.map((link) => (
									<p key={link.label}>
										<a href={link.url} target="_blank" rel="noopener noreferrer">
											{link.label}
										</a>
									</p>
								))}
							</div>
						</div>
					)}
				</div>
				<div className="p-4 flex-1 nine-task-overrides border border-gray-300 rounded bg-white shadow-sm">
					<MarkdownViewer value={welcomeMessage} />
				</div>
			</div>
			<div className="mt-4 max-w-[1024px] w-full">
				<div className="mt-2  border border-gray-300 rounded bg-white shadow-sm">
					<div className="flex justify-between items-center border-b border-black/89 p-4">
						<p className="font-bold">Internal Contacts</p>
						<Button
							onClick={() => handleOpenPartnerCompanyContactDialog()}
							className="bg-transparent border border-black text-black hover:bg-black/5"
						>
							<Plus /> Add Contact
						</Button>
					</div>
					<DataGrid
						data={partnerCompany.contacts || []}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						pagination
						rowCount={(partnerCompany.contacts || []).length}
						onReload={() => queryClient.invalidateQueries({ queryKey: ['partner-company', partnerId] })}
					/>
				</div>
			</div>
			<AddOwnerForm isOpen={isAddOwnerDialogOpen} onClose={() => setIsAddOwnerDialogOpen(false)} onSubmit={addOwners} />
			<PartnerCompanyContactForm
				isOpen={isPartnerCompanyContactDialogOpen}
				onClose={handleCloseGroupContactDialog}
				onAdd={addPartnerCompanyContact}
				onUpdate={updatePartnerCompanyContact}
				data={partnerCompanyContactData}
			/>
			<FormDialog
				isOpen={isDeletePartnerCompanyContactDialogOpen}
				onClose={handleCloseDeletePartnerCompanyContactDialog}
				title="Delete contact information"
				description="Are you sure you want to delete this contact information?"
				buttons={
					<>
						<Button variant="outline" className="bg-transparent" onClick={handleCloseDeletePartnerCompanyContactDialog}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={deletePartnerCompanyContact}>
							Delete contact
						</Button>
					</>
				}
			></FormDialog>
		</div>
	);
}
